<script lang="ts">
    import type { PostsByCategoryQuery, PostTeaserFragment, SubmissionTeaserFragment } from '$gen/graphql';
    import { page } from '$app/stores';
    import PostTeaser from '$lib/components/PostTeaser.svelte';
    import SeoMetaTags from '$lib/components/SeoMetaTags.svelte';
    import { categoryPostFetchLimit } from '$lib/constants';
    import { mergePostAndSubmissionsByDate } from '$lib/post-and-submission-merge';
    import { routes } from '$lib/routes';
    import { isPostTeaserFragment, isSubmissionTeaserFragment } from '$lib/types/type-predicates';
    import { throttle } from 'lodash-es';

    export let query: PostsByCategoryQuery;

    let offset = categoryPostFetchLimit;
    let extraPosts: PostTeaserFragment[] = [];
    let extraSubmissions: SubmissionTeaserFragment[] = [];
    // Assume there is more as long as the last fetch returned a full page of posts or submissions.
    let hasMore = true;

    // Reset the loaded entries when navigating to another category.
    $: $page.params.category, resetExtraPosts();
    function resetExtraPosts() {
        offset = categoryPostFetchLimit;
        extraPosts = [];
        extraSubmissions = [];
        hasMore =
            query?.posts.length >= categoryPostFetchLimit ||
            query?.submissions.length >= categoryPostFetchLimit;
    }

    $: posts = [].concat(query?.posts.filter(isPostTeaserFragment) ?? [], extraPosts);
    $: submissions = [].concat(
        query?.submissions.filter(isSubmissionTeaserFragment) ?? [],
        extraSubmissions
    );
    $: mixedPosts = mergePostAndSubmissionsByDate(posts, submissions);
    $: seo = query?.category?.__typename === 'postCategory_Category' ? query.category.seo : null;

    async function fetchMorePosts() {
        const resp = await fetch(routes.api_loadMoreCategoryPosts($page.params.category, offset));
        const json = await resp.json();
        const more: PostsByCategoryQuery = json.query;

        if (more) {
            extraPosts = extraPosts.concat(more.posts.filter(isPostTeaserFragment));
            extraSubmissions = extraSubmissions.concat(
                more.submissions.filter(isSubmissionTeaserFragment)
            );
            offset += categoryPostFetchLimit;
            hasMore =
                more.posts.length >= categoryPostFetchLimit ||
                more.submissions.length >= categoryPostFetchLimit;
        }
    }

    const fetchMorePostsThrottled = throttle(fetchMorePosts, 2000);
</script>

<SeoMetaTags {seo} />

<section class="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
    {#each mixedPosts as post (post.id)}
        <PostTeaser {post} />
    {/each}
</section>

{#if hasMore}
    <button class="newbutton cl" on:click={fetchMorePostsThrottled}>Load more</button>
{/if}
